import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditLimitModal } from '../components/CreditLimitModal';
import { TierLimitModal } from '../components/TierLimitModal';
import { setGlobalCreditLimitHandler, setGlobalUnauthorizedHandler } from '../utils/api';
import { useAuth } from './AuthContext';

interface ErrorHandlerContextType {
  showCreditLimitModal: () => void;
  showTierLimitModal: (limitType: 'project' | 'document' | 'resource' | 'storage') => void;
  CreditLimitModalComponent: ReactNode;
  TierLimitModalComponent: ReactNode;
}

const ErrorHandlerContext = createContext<ErrorHandlerContextType | undefined>(undefined);

export const useErrorHandler = (): ErrorHandlerContextType => {
  const context = useContext(ErrorHandlerContext);
  if (!context) {
    throw new Error('useErrorHandler must be used within ErrorHandlerProvider');
  }
  return context;
};

interface ErrorHandlerProviderProps {
  children: ReactNode;
}

export const ErrorHandlerProvider: React.FC<ErrorHandlerProviderProps> = ({ children }) => {
  const [creditLimitModalOpen, setCreditLimitModalOpen] = useState(false);
  const [tierLimitModalOpen, setTierLimitModalOpen] = useState(false);
  const [tierLimitType, setTierLimitType] = useState<'project' | 'document' | 'resource' | 'storage'>('project');
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const showCreditLimitModal = useCallback(() => {
    setCreditLimitModalOpen(true);
  }, []);

  const showTierLimitModal = useCallback((limitType: 'project' | 'document' | 'resource' | 'storage') => {
    setTierLimitType(limitType);
    setTierLimitModalOpen(true);
  }, []);

  React.useEffect(() => {
    setGlobalCreditLimitHandler(showCreditLimitModal);
    return () => {
      setGlobalCreditLimitHandler(null);
    };
  }, [showCreditLimitModal]);

  React.useEffect(() => {
    const unauthorizedHandler = (redirectPath?: string, options?: { accountDisabled?: boolean }): void => {
      const currentPath = redirectPath || `${location.pathname}${location.search}`;

      logout();

      const isAuthRoute =
        location.pathname === '/login' ||
        location.pathname === '/register' ||
        location.pathname === '/forgot-password' ||
        location.pathname === '/reset-password';

      if (options?.accountDisabled) {
        navigate('/login?accountDisabled=1');
        return;
      }

      if (!isAuthRoute && currentPath && currentPath.startsWith('/')) {
        navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      } else {
        navigate('/login');
      }
    };

    setGlobalUnauthorizedHandler(unauthorizedHandler);

    return () => {
      setGlobalUnauthorizedHandler(null);
    };
  }, [location.pathname, location.search, logout, navigate]);

  const value: ErrorHandlerContextType = {
    showCreditLimitModal,
    showTierLimitModal,
    CreditLimitModalComponent: (
      <CreditLimitModal open={creditLimitModalOpen} onOpenChange={setCreditLimitModalOpen} />
    ),
    TierLimitModalComponent: (
      <TierLimitModal
        open={tierLimitModalOpen}
        onOpenChange={setTierLimitModalOpen}
        limitType={tierLimitType}
      />
    ),
  };

  return (
    <ErrorHandlerContext.Provider value={value}>
      {children}
      {value.CreditLimitModalComponent}
      {value.TierLimitModalComponent}
    </ErrorHandlerContext.Provider>
  );
};

